// .pen: I7fQN4/jX0dU (ds/disclosure/accordion--collapsed/--expanded), used
// for all 9 sections of Camada 4. "Organização de categorias"
// (CABJE/sSEjT/sNO52/f5QVi/QTKvX/P9lFEt) and "Avaliação completa"
// (v5MbvF/c1o5i/kLWby/sJEuO/i2e5oy/Cq32i) bodies are now modeled in the
// `.pen` — divider-separated list via `ReportAssessCard`, plus a
// `ds/message--neutral` note ahead of the category-organization list.
import { CaretDown, Info, Sparkle } from '@phosphor-icons/react';

import { SelfieReportCharts } from '../SelfieReportCharts';
import {
  ReportAssessCard,
  ReportExpenseRow,
  ReportHero,
  ReportMessage,
  ReportProgressList,
  ReportSectionHeading,
  ReportSummaryGrid,
} from './ReportPrimitives';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { cn } from '../../../utils/cn';
import pt from '../../../locales/pt';
import { formatEUR } from '../../../utils/money';
import { normalizeAssessment } from '../../../lib/reportUiUtils';

function formatDatePt(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDelta(value) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  const sign = n > 0 ? '+' : '';
  return `${sign}${formatEUR(n)}`;
}

function deltaClass(value) {
  if (value === null || value === undefined) return 'text-muted-foreground/70';
  const n = Number(value);
  if (n > 0) return 'text-success-fg';
  if (n < 0) return 'text-warning-fg';
  return 'text-muted-foreground/70';
}

function buildCycleFinanceItems(report) {
  const summary = report?.cycle_summary || {};
  const income = summary.income_eur ?? report?.patrimonio?.income_eur;
  const expenses = summary.expenses_eur ?? report?.patrimonio?.expenses_eur;
  const margin = summary.margin_eur ?? report?.patrimonio?.margin_eur;
  const savingsRateRaw = summary.savings_rate_pct ?? report?.patrimonio?.savings_rate_pct;
  const incomeNum = income != null ? Number(income) : null;
  const savingsRate =
    incomeNum != null && incomeNum > 0 && savingsRateRaw != null ? Number(savingsRateRaw) : null;
  const marginNum = margin != null ? Number(margin) : null;

  return [
    { label: pt.snapshots.report.metrics.cycleIncome, value: income != null ? formatEUR(income) : '—' },
    { label: pt.snapshots.report.metrics.cycleExpenses, value: expenses != null ? formatEUR(expenses) : '—' },
    {
      label: pt.snapshots.report.metrics.cycleMargin,
      value: margin != null ? formatEUR(margin) : '—',
      subTone: marginNum == null ? undefined : marginNum >= 0 ? 'positive' : 'negative',
    },
    {
      label: pt.snapshots.report.metrics.savingsRate,
      value: savingsRate != null && Number.isFinite(savingsRate) ? `${savingsRate.toFixed(1)}%` : '—',
      subTone:
        report?.patrimonio?.savings_rate_delta_pp != null
          ? report.patrimonio.savings_rate_delta_pp < 0
            ? 'warning'
            : 'positive'
          : marginNum != null
            ? marginNum >= 0
              ? 'positive'
              : 'negative'
            : undefined,
    },
  ];
}

/**
 * .pen: yH6mZ (--collapsed) / yVQhj (--expanded) — item do accordion.
 * `<details>` nativo preservado por acessibilidade; visual alinhado ao .pen.
 */
function DetailSection({ title, subtitle, children, defaultOpen = false }) {
  return (
    <details
      className="group rounded-ds-lg border border-border bg-card open:border-sage-border print:open"
      open={defaultOpen ? true : undefined}
    >
      <summary className="flex cursor-pointer list-none items-center gap-space-3 px-space-4 py-space-3 marker:content-none [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <p className="text-type-body font-semibold text-foreground">{title}</p>
          {subtitle ? <p className="text-type-body-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
        <CaretDown
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="border-t border-border px-space-4 pb-space-4 pt-space-3">{children}</div>
    </details>
  );
}

/**
 * Camada 4 — números completos colapsados por defeito.
 */
export function SelfieReportDetailsAccordion({ report, verdictTone = 'mixed' }) {
  const r = report;
  const assessment = normalizeAssessment(r.assessment);
  const netDelta = r.patrimonio?.delta_eur;
  const netDeltaPct = r.patrimonio?.delta_pct;

  const heroChange =
    netDelta != null
      ? `${formatDelta(netDelta)}${netDeltaPct != null ? ` · ${netDeltaPct > 0 ? '+' : ''}${netDeltaPct}%` : ''} ${pt.snapshots.report.metrics.vsPrevious}`
      : null;

  const summaryItems = [
    {
      label: pt.snapshots.report.metrics.assets,
      value: formatEUR(r.patrimonio?.assets_total),
      sub:
        r.patrimonio?.assets_delta != null
          ? `${formatDelta(r.patrimonio.assets_delta)} ${pt.snapshots.report.metrics.vsPrevious}`
          : null,
      subTone: r.patrimonio?.assets_delta > 0 ? 'positive' : r.patrimonio?.assets_delta < 0 ? 'negative' : undefined,
    },
    {
      label: pt.snapshots.report.metrics.liabilities,
      value: formatEUR(r.patrimonio?.liabilities_total),
      sub:
        r.patrimonio?.liabilities_delta != null
          ? `${formatDelta(r.patrimonio.liabilities_delta)} ${pt.snapshots.report.metrics.vsPrevious}`
          : null,
      subTone: r.patrimonio?.liabilities_delta < 0 ? 'positive' : r.patrimonio?.liabilities_delta > 0 ? 'negative' : undefined,
    },
  ];

  const assetRows = (r.assets || []).filter(
    (row) => Number(row.curr_value || 0) > 0 || Number(row.prev_value || 0) > 0,
  );
  const liabilityRows = (r.liabilities || []).filter(
    (row) => Number(row.curr_value || 0) > 0 || Number(row.prev_value || 0) > 0,
  );

  const budgetCategories = r.categories || [];
  const budgetItems = budgetCategories.slice(0, 12).map((cat) => ({
    id: cat.name,
    name: cat.name,
    status: cat.status === 'exceeded' ? 'exceeded' : cat.status === 'no_budget' ? 'no_budget' : 'ok',
    value: {
      pct: cat.spent_pct,
      label: `${formatEUR(cat.spent_eur)} / ${formatEUR(cat.budget_eur ?? 0)}`,
    },
  }));
  const exceededCount = budgetCategories.filter((cat) => cat.status === 'exceeded').length;
  const budgetFootnote = budgetCategories.length
    ? exceededCount
      ? `${exceededCount} ${exceededCount === 1 ? 'categoria passou' : 'categorias passaram'} o limite este ciclo · ${budgetCategories.length - exceededCount} outras dentro do previsto`
      : 'Todas as categorias dentro do orçamento este ciclo.'
    : null;

  const goalItems = (r.goals || []).map((goal) => ({
    id: goal.name,
    name: goal.name,
    status: 'ok',
    value: {
      pct: goal.progress_pct,
      label: `${formatEUR(goal.current_amount)} de ${formatEUR(goal.target_amount)}`,
    },
  }));
  const goalAvgPct = goalItems.length
    ? Math.round(goalItems.reduce((sum, g) => sum + Number(g.value.pct || 0), 0) / goalItems.length)
    : null;
  const goalFootnote = goalAvgPct != null ? `Progresso médio de ${goalAvgPct}% nos teus objetivos ativos.` : null;

  const topExpenses = r.top_expenses || [];
  const maxExpense = topExpenses.length ? Number(topExpenses[0].amount || 0) : 0;
  const topExpensesTotal = topExpenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
  const cycleExpensesTotal = r.charts?.stats?.total_eur;
  const topExpensesFootnote = topExpenses.length
    ? `Top ${topExpenses.length} · ${formatEUR(topExpensesTotal)}${
        cycleExpensesTotal ? ` · ${Math.round((topExpensesTotal / cycleExpensesTotal) * 100)}% do ciclo` : ''
      }`
    : null;

  const hasCharts = r.timeline?.length > 1 || (r.charts?.stats?.total_eur || 0) > 0;
  const categoriesTotal = (r.categories || []).reduce((sum, cat) => sum + Number(cat.spent_eur || 0), 0);
  const categoryCount = (r.categories || []).length;

  const disclaimer =
    verdictTone === 'cautious' ? pt.snapshots.report.disclaimer.cautious : pt.snapshots.report.disclaimer.default;

  return (
    <section aria-labelledby="report-details-heading" className="print:block">
      <ReportSectionHeading id="report-details-heading" eyebrow={pt.snapshots.report.layers.details} />

      <div className="mt-space-3 flex flex-col gap-space-2 print:[&_details]:open">
        <DetailSection
          title={pt.snapshots.report.sections.patrimonio}
          subtitle={`${formatEUR(r.patrimonio?.current)} · ${pt.snapshots.report.sections.assets} e ${pt.snapshots.report.sections.liabilities.toLowerCase()}`}
        >
          <div className="flex flex-col gap-space-4">
            <ReportHero
              label={pt.snapshots.ritual.confirm.netLabel}
              value={r.patrimonio?.current}
              changeLabel={heroChange}
              changePositive={netDelta == null || netDelta >= 0}
            />
            <ReportSummaryGrid items={summaryItems} columns={2} />
          </div>
        </DetailSection>

        <DetailSection
          title={pt.snapshots.report.sections.cycleFinance}
          subtitle={pt.snapshots.report.cycleFinanceIntro}
        >
          <ReportSummaryGrid items={buildCycleFinanceItems(r)} columns={2} />
        </DetailSection>

        {hasCharts ? (
          <DetailSection title={pt.snapshots.report.sections.charts} subtitle={pt.snapshots.report.charts.sectionIntro}>
            <SelfieReportCharts charts={r.charts} timeline={r.timeline} />
          </DetailSection>
        ) : null}

        {assetRows.length || liabilityRows.length ? (
          <DetailSection
            title={pt.snapshots.report.sections.allocation}
            subtitle={pt.snapshots.report.allocationIntro}
          >
            <div className="flex flex-col gap-space-4">
              {assetRows.length ? (
                <div className="overflow-hidden rounded-ds-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{pt.snapshots.report.table.asset}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.previous}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.current}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.change}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assetRows.map((row) => (
                        <TableRow key={row.name}>
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="text-right font-mono text-type-body-sm text-muted-foreground">
                            {formatEUR(row.prev_value)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-type-body-sm">
                            {formatEUR(row.curr_value)}
                          </TableCell>
                          <TableCell className={cn('text-right font-mono text-type-body-sm font-medium', deltaClass(row.delta))}>
                            {formatDelta(row.delta)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
              {liabilityRows.length ? (
                <div className="overflow-hidden rounded-ds-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{pt.snapshots.report.sections.liabilities}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.previous}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.current}</TableHead>
                        <TableHead className="text-right">{pt.snapshots.report.table.change}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {liabilityRows.map((row) => (
                        <TableRow key={row.name}>
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="text-right font-mono text-type-body-sm text-muted-foreground">
                            {formatEUR(row.prev_value)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-type-body-sm">
                            {formatEUR(row.curr_value)}
                          </TableCell>
                          <TableCell className={cn('text-right font-mono text-type-body-sm font-medium', deltaClass(row.delta))}>
                            {formatDelta(row.delta)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </div>
          </DetailSection>
        ) : null}

        {budgetItems.length ? (
          <DetailSection
            title={pt.snapshots.report.sections.categories}
            subtitle={categoryCount ? `${categoryCount} categorias · ${formatEUR(categoriesTotal)} total` : pt.snapshots.report.layers.tapToExpand}
          >
            <ReportProgressList items={budgetItems} footnote={budgetFootnote} />
          </DetailSection>
        ) : null}

        {topExpenses.length ? (
          <DetailSection title={pt.snapshots.report.sections.topExpenses} subtitle={pt.snapshots.report.layers.tapToExpand}>
            <div className="flex flex-col">
              {topExpenses.map((exp) => (
                <ReportExpenseRow
                  key={exp.rank}
                  title={exp.description}
                  subtitle={`${exp.date ? `${formatDatePt(exp.date)} · ` : ''}${exp.category}`}
                  amount={`− ${formatEUR(exp.amount)}`}
                  barWidth={maxExpense ? (Number(exp.amount || 0) / maxExpense) * 100 : 0}
                />
              ))}
            </div>
            {topExpensesFootnote ? (
              <p className="mt-space-3 border-t border-border pt-space-3 text-type-label text-muted-foreground">
                {topExpensesFootnote}
              </p>
            ) : null}
          </DetailSection>
        ) : null}

        {goalItems.length ? (
          <DetailSection title={pt.snapshots.report.sections.goals} subtitle={pt.snapshots.report.layers.tapToExpand}>
            <ReportProgressList items={goalItems} footnote={goalFootnote} />
          </DetailSection>
        ) : null}

        {r.category_organization?.status === 'needs_review' && r.category_organization.notes?.length ? (
          <DetailSection
            title={pt.snapshots.report.sections.categoryOrganization}
            subtitle={r.category_organization.summary || pt.snapshots.report.layers.tapToExpand}
          >
            {r.category_organization.summary ? (
              <ReportMessage
                tone="neutral"
                icon={Sparkle}
                title={pt.snapshots.report.sections.categoryOrganizationNoteTitle}
                className="mb-space-3"
              >
                {r.category_organization.summary}
              </ReportMessage>
            ) : null}
            <div className="flex flex-col">
              {r.category_organization.notes.map((note, idx) => (
                <ReportAssessCard
                  key={note.category_name}
                  first={idx === 0}
                  tone="improve"
                  title={note.category_name}
                  body={note.body}
                />
              ))}
            </div>
          </DetailSection>
        ) : null}

        {assessment.good.length || assessment.bad.length || assessment.improve.length ? (
          <DetailSection title={pt.snapshots.report.sections.assessment} subtitle={pt.snapshots.report.layers.tapToExpand}>
            <div className="flex flex-col">
              {[
                ...assessment.good.map((item) => ({ ...item, tone: 'good' })),
                ...assessment.bad.map((item) => ({ ...item, tone: 'bad' })),
                ...assessment.improve.map((item) => ({ ...item, tone: 'improve' })),
              ].map((item, idx) => (
                <ReportAssessCard
                  key={`${item.tone}-${idx}`}
                  first={idx === 0}
                  tone={item.tone}
                  title={item.title}
                  body={item.body}
                />
              ))}
            </div>
          </DetailSection>
        ) : null}
      </div>

      <p className="mt-space-4 flex items-start gap-2 px-1 text-center text-type-label leading-relaxed text-muted-foreground print:mt-8">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{disclaimer}</span>
      </p>
    </section>
  );
}

export default SelfieReportDetailsAccordion;
